import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import api from "@/lib/api";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";

export default function Properties() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQRaw] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const setQ = (v) => { setQRaw(v); setPage(1); }; // a new search always starts at page 1
  const navigate = useNavigate();

  useEffect(() => {
    const params = { page, page_size: PAGE_SIZE, ...(q ? { q } : {}) };
    api.get("/directory/properties", { params }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [q, page]);

  if (error) return <ErrorState testId="properties-error" />;

  return (
    <div data-testid="properties-page" className="space-y-5">
      <div>
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">Hotels &amp; Properties</h1>
        <p className="text-sm text-jigsaw-muted">All accommodation properties across every supplier.</p>
      </div>

      <Input data-testid="properties-search-input" placeholder="Search properties..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />

      {!data ? (
        <LoadingState testId="properties-loading" />
      ) : data.items.length === 0 ? (
        <EmptyState testId="properties-empty" icon={Building2} title="No properties found" />
      ) : (
        <div>
          <StackedList testId="properties-stacked" items={data.items} title={(p) => p.name} onOpen={(p) => navigate(`/control-centre/properties/${p.id}`)} openLabel="Open property"
            fields={[{ label: "Supplier", value: (p) => p.supplier_name || (p.ownership === "UNKNOWN" ? "Ownership unknown (legacy)" : "—"), full: true }, { label: "City", value: (p) => p.city }, { label: "Postcode", value: (p) => p.postcode, mono: true }, { label: "Type", value: (p) => p.property_type, full: true }]} />
          <div className="hidden sm:block">
          <Table data-testid="properties-table">
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Postcode</TableHead>
                <TableHead>Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <TableRow key={p.id} data-testid={`property-row-${p.id}`} className="cursor-pointer hover:bg-jigsaw-bg" onClick={() => navigate(`/control-centre/properties/${p.id}`)}>
                  <TableCell className="font-medium text-jigsaw-navy">{p.name}</TableCell>
                  <TableCell>{p.supplier_name || (p.ownership === "UNKNOWN" ? <span data-testid="property-ownership-unknown" className="rounded-full border border-jigsaw-border px-2 py-0.5 text-xs text-jigsaw-muted">Ownership unknown · legacy</span> : "—")}</TableCell>
                  <TableCell>{p.city || "—"}</TableCell>
                  <TableCell className="font-mono-ref">{p.postcode || "—"}</TableCell>
                  <TableCell className="text-xs text-jigsaw-muted">{p.property_type || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="properties-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
